import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavOptions from "./NavOptions";
import colors from "../../theme/Theme";
import "./AppBar.css";

const logoUrl = "/images/logo.png";
const MOBILE_BREAKPOINT = 768;

const getIsMobileViewport = () => {
  if (typeof window === "undefined") return false;

  const mediaQueryMatch = window.matchMedia?.(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
  const visualViewportMatch =
    typeof window.visualViewport?.width === "number" &&
    window.visualViewport.width <= MOBILE_BREAKPOINT;
  const innerWidthMatch = window.innerWidth <= MOBILE_BREAKPOINT;

  return mediaQueryMatch || visualViewportMatch || innerWidthMatch;
};

const AppBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState("Inicio");
  const [isMobile, setIsMobile] = useState(() => getIsMobileViewport());
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith("/productos")) {
      setActiveItem("Productos");
      return;
    }

    if (location.pathname.startsWith("/ecommerce") || location.pathname.startsWith("/mayorista")) {
      setActiveItem("Comprar");
      return;
    }

    const pathToItem = {
      "/": "Inicio",
      "/nosotros": "Nosotros",
      "/preguntas-frecuentes": "Preguntas frecuentes",
      "/contacto": "Contacto",
    };

    setActiveItem(pathToItem[location.pathname] || "Inicio");
  }, [location.pathname]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);

    const syncMobileState = () => {
      const mobile = getIsMobileViewport();
      setIsMobile(mobile);
      setMenuOpen((previous) => (mobile ? previous : false));
    };

    syncMobileState();
    const rafId = window.requestAnimationFrame(syncMobileState);

    mediaQuery.addEventListener?.("change", syncMobileState);
    mediaQuery.addListener?.(syncMobileState);
    window.addEventListener("resize", syncMobileState);
    window.addEventListener("orientationchange", syncMobileState);
    window.visualViewport?.addEventListener("resize", syncMobileState);
    window.addEventListener("load", syncMobileState);

    return () => {
      window.cancelAnimationFrame(rafId);
      mediaQuery.removeEventListener?.("change", syncMobileState);
      mediaQuery.removeListener?.(syncMobileState);
      window.removeEventListener("resize", syncMobileState);
      window.removeEventListener("orientationchange", syncMobileState);
      window.visualViewport?.removeEventListener("resize", syncMobileState);
      window.removeEventListener("load", syncMobileState);
    };
  }, []);

  const handleItemClick = (item, specificPath = null) => {
    const itemToPath = {
      Inicio: "/",
      Comprar: "/mayorista",
      Productos: "/productos/FibraDeCarbono",
      Nosotros: "/nosotros",
      "Preguntas frecuentes": "/preguntas-frecuentes",
      Contacto: "/contacto",
    };

    navigate(specificPath || itemToPath[item] || "/");
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });

    setActiveItem(item);
    if (isMobile) setMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate("/");
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    setActiveItem("Inicio");
    if (isMobile) setMenuOpen(false);
  };

  return (
    <header className="app-bar" style={{ backgroundColor: colors.black }}>
      <div className="app-bar-container">
        <div className="logo-container" onClick={handleLogoClick}>
          <img src={logoUrl} alt="Logo" className="app-bar-logo" />
        </div>

        {!isMobile && <NavOptions activeItem={activeItem} onItemClick={handleItemClick} />}

        {isMobile && (
          <>
            <button
              className={`hamburger-btn ${menuOpen ? "open" : ""}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <div className="bar" />
              <div className="bar" />
              <div className="bar" />
            </button>

            {menuOpen && (
              <div className="mobile-menu">
                <NavOptions activeItem={activeItem} onItemClick={handleItemClick} isMobile />
              </div>
            )}
          </>
        )}
      </div>
    </header>
  );
};

export default AppBar;
